import { useQuery } from 'react-query'
import { GeneralService } from '../General.service'
import { STALE_TIMES } from '@/constants'
import { StorageKeys } from '@/contexts/Auth/types'
import useUserPermissionsStore from '@/store/UserPermissions/UserPermissionsStore'

export const useGetClinicList = (params?: string) => {
  const encondeParams = params ? encodeURIComponent(params) : ''
  const { data } = useQuery(
    ['getClinicList', `getClinicList-${encondeParams}`],
    () => GeneralService.getClinicList(encondeParams),
    {
      enabled: Boolean(params?.length >= 3),
    },
  )

  return {
    clinicList: data,
  }
}

export const useGetOwnershipTypes = () => {
  const { data } = useQuery(
    ['ownershipTypes'],
    GeneralService.getOwnershipTypes,
    {
      staleTime: STALE_TIMES.DAY,
    },
  )

  return {
    ownershipTypes: data,
  }
}

export const useGetGenderList = () => {
  const { data, isLoading } = useQuery(
    ['genderList'],
    GeneralService.getGenderList,
    {
      staleTime: STALE_TIMES.DAY,
    },
  )

  return {
    genderList: data,
    genderListIsLoading: isLoading,
  }
}

export const useGetRelationshipList = () => {
  const { data, isLoading } = useQuery(
    ['getRelationshipList'],
    GeneralService.getRelationshipList,
    {
      staleTime: STALE_TIMES.DAY,
    },
  )

  return {
    relationshipList: data,
    relationshipListIsLoading: isLoading,
  }
}

export const useGetRelationshipContactEmergencyList = () => {
  const { data, isLoading } = useQuery(
    ['relationshipContactEmergencyList'],
    GeneralService.getRelationshipContactEmergencyList,
    {
      staleTime: STALE_TIMES.DAY,
    },
  )

  return {
    relationshipContactEmergencyList: data,
    relationshipContactEmergencyListIsLoading: isLoading,
  }
}

export const useAddressByCep = (cep?: string) => {
  const { data, isLoading } = useQuery(
    ['addressByCep', `addressByCep-${cep}`],
    () => GeneralService.getAddressByCep(cep),
    {
      enabled: cep?.length === 9,
    },
  )

  return {
    addressByCep: data,
    addressByCepIsLoading: isLoading,
  }
}

export const useGetBooleanList = () => {
  const { data, isLoading } = useQuery(
    ['getBooleanList'],
    GeneralService.getBooleanList,
    {
      staleTime: STALE_TIMES.DAY,
    },
  )

  return {
    booleanList: data,
    booleanListIsLoading: isLoading,
  }
}

export const useGetStateList = () => {
  const { data, isLoading } = useQuery(
    ['getStateList'],
    GeneralService.getStateList,
    {
      staleTime: STALE_TIMES.DAY,
    },
  )

  return {
    stateList: data,
    stateListIsLoading: isLoading,
  }
}

export const useGetGenderIdentityList = () => {
  const { data, isLoading } = useQuery(
    ['getGenderIdentityList'],
    GeneralService.getGenderIdentityList,
    {
      staleTime: STALE_TIMES.DAY,
    },
  )

  return {
    genderIdentityList: data,
    genderIdentityListIsLoading: isLoading,
  }
}

export const useGetRaceColorList = () => {
  const { data, isLoading } = useQuery(
    ['getRaceColorList'],
    GeneralService.getRaceColorList,
    {
      staleTime: STALE_TIMES.DAY,
    },
  )

  return {
    raceColorList: data,
    raceColorListIsLoading: isLoading,
  }
}

export const useGetReligionList = () => {
  const { data, isLoading } = useQuery(
    ['getReligionList'],
    GeneralService.getReligionList,
    {
      staleTime: STALE_TIMES.DAY,
    },
  )

  return {
    religionList: data,
    religionListIsLoading: isLoading,
  }
}

export const useGetEducationList = () => {
  const { data, isLoading } = useQuery(
    ['getEducationList'],
    GeneralService.getEducationList,
    {
      staleTime: STALE_TIMES.DAY,
    },
  )

  return {
    educationList: data,
    educationListIsLoading: isLoading,
  }
}

export const useGetLivingAreaList = () => {
  const { data, isLoading } = useQuery(
    ['getLivingAreaList'],
    GeneralService.getLivingAreaList,
    {
      staleTime: STALE_TIMES.DAY,
    },
  )

  return {
    livingAreaList: data,
    livingAreaListIsLoading: isLoading,
  }
}

export const useGetCboListByName = (params?: string) => {
  const { data, isLoading } = useQuery(
    ['cboListByName', `cboListByName-${params}`],
    () => GeneralService.getCboListByName(params),
    {
      enabled: Boolean(params),
    },
  )

  return {
    cboList: data,
    cboListIsLoading: isLoading,
  }
}

export const useGetCboListById = (params?: string | number) => {
  const { data, isLoading } = useQuery(
    ['cboListByName', `cboListByName-${params}`],
    () => GeneralService.getCboListById(params),
    {
      enabled: Boolean(params),
    },
  )

  return {
    cboList: data,
    cboListIsLoading: isLoading,
  }
}

export const useGetNationalityList = () => {
  const { data, isLoading } = useQuery(
    ['getNationalityList'],
    GeneralService.getNationalityList,
    {
      staleTime: STALE_TIMES.DAY,
    },
  )

  return {
    nationalityList: data,
    nationalityListIsLoading: isLoading,
  }
}

export const useGetCountryList = () => {
  const { data, isLoading } = useQuery(
    ['getCountryList'],
    GeneralService.getCountryList,
    {
      staleTime: STALE_TIMES.DAY,
    },
  )

  return {
    countryList: data,
    countryListIsLoading: isLoading,
  }
}

export const useGetHealthPlanHolderByNameOrCpf = (
  params?: string,
  idClinic?: string | number,
) => {
  const { data, isLoading } = useQuery(
    ['healthPlanHolderByNameOrCpf', `healthPlanHolderByNameOrCpf-${params}`],
    () => GeneralService.getHealthPlanHolderByNameOrCpf(params, idClinic),
    {
      enabled: Boolean(params) && Boolean(idClinic) && !params?.includes('|'),
    },
  )

  return {
    healthPlanHolderByNameOrCpf: data,
    healthPlanHolderByNameOrCpfIsLoading: isLoading,
  }
}

export const useGetConexaAdminPermissions = () => {
  const userId = JSON.parse(localStorage.getItem(StorageKeys.userId))
  const { updateValue } = useUserPermissionsStore()

  const { data, isSuccess, isLoading } = useQuery(
    ['permissions', userId],
    () => GeneralService.getConexaAdminPermissions(userId),
    {
      enabled: Boolean(userId),
      refetchOnWindowFocus: false,
      onSuccess: data => {
        updateValue('userPermissions', data)
      },
    },
  )

  return {
    conexaAdminPermissions: data,
    conexaAdminPermissionsIsSuccess: isSuccess,
    conexaAdminPermissionsIsLoading: isLoading,
  }
}
